//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material'
import React, { FC } from 'react'

import { Delete } from '@mui/icons-material'

import ButtonModal from '@react/components/ButtonModal'
import ViewField from '@react/components/elements/ViewField'
import { poolOffchainDataPut } from '@react/fetchers/PoolFetchers'
import { useOtChainState } from '@react/redux/store'
import PoolOffchainCollateralForm from './PoolOffchainCollateralForm'

import { CollateralCTO, CollateralSchema, PoolOffchainDataCTO } from '@shared/PoolOffchainDataCTO'
import { logOT } from '@shared/loggingShared'

export const PoolOffchainCollateralView: FC<{
  poolOffchainDataCTO: PoolOffchainDataCTO
  dateUpdatedCallback: () => void
}> = ({ poolOffchainDataCTO, dateUpdatedCallback }): React.ReactElement => {
  const {
    cognitoUser,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()
  const {
    chainConfiguration: { chainConfigurationName },
  } = useOtChainState()
  const theme = useTheme()

  const updateCollateral = (collateral: CollateralCTO, index: number) => {
    logOT.info('updateCollateral', collateral, index)
    if (serverApiBaseUrl && restAPIKey && (cognitoUser || chainConfigurationName === 'Hardhat')) {
      if (index < 0) {
        poolOffchainDataCTO.collateral.push(collateral)
      } else {
        poolOffchainDataCTO.collateral[index] = collateral
      }
      logOT.info('updateCollateral put', poolOffchainDataCTO)
      poolOffchainDataPut(serverApiBaseUrl, restAPIKey, poolOffchainDataCTO, cognitoUser!)
        .then(() => {
          logOT.info('updateCollateral dateUpdatedCallback')
          dateUpdatedCallback()
        })
        .catch((err) => {
          console.error('Error updating pool offchain data', err)
        })
    }
  }
  const deleteItemCommand = (index: number) => () => {
    poolOffchainDataCTO.collateral.splice(index, 1)

    if (serverApiBaseUrl && restAPIKey && cognitoUser) {
      poolOffchainDataPut(serverApiBaseUrl, restAPIKey, poolOffchainDataCTO, cognitoUser)
        .then(() => {
          dateUpdatedCallback()
          const event = new Event('modalClose')
          window.dispatchEvent(event)
        })
        .catch((err) => {
          console.error('Error updating pool offchain data', err)
        })
    }
  }

  function CollateralLine(props: { collateral: CollateralCTO; index: number }): React.ReactElement {
    const { collateral, index } = props
    return (
      <Grid container spacing={1} sx={{ marginTop: 4 }}>
        <Grid item sm={2}>
          <ButtonModal title={'Edit'} variant="primary">
            <PoolOffchainCollateralForm updateCollateral={updateCollateral} collateralCTO={collateral} index={index} />
          </ButtonModal>
        </Grid>
        <Grid item sm={4}>
          <ViewField
            dataObject={collateral}
            fieldSchema={CollateralSchema.describe().fields.collateralType}
          ></ViewField>
        </Grid>
        <Grid item sm={5}>
          <ViewField dataObject={collateral} fieldSchema={CollateralSchema.describe().fields.collateralId}></ViewField>
        </Grid>
        <Grid item sm={1}>
          <IconButton
            onClick={deleteItemCommand(index)}
            sx={{ height: '28px', width: '28px', padding: 6, float: 'right', color: theme.palette.primary.main }}
          >
            <Delete sx={{ width: '28px', height: '28px', color: theme.palette.fg.muted }} />
          </IconButton>
        </Grid>
      </Grid>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          height: '50px',
          backgroundColor: theme.palette.background.subtle,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0px',
          gap: '8px',
          paddingLeft: '16px',

          paddingRight: '16px',
          borderRadius: 1,
        }}
      >
        <Typography variant={'bodyBold'}>Collateral</Typography>
        {poolOffchainDataCTO && (
          <ButtonModal title={'Add Item'} variant="primary">
            <PoolOffchainCollateralForm
              index={-1}
              updateCollateral={updateCollateral}
              collateralCTO={{ collateralType: '', collateralId: '' } as CollateralCTO}
            />
          </ButtonModal>
        )}
      </Box>

      {poolOffchainDataCTO && (
        <Grid container spacing={1} sx={{ marginTop: 4 }}>
          {poolOffchainDataCTO.collateral.map((collateral: CollateralCTO, index: number) => {
            return <CollateralLine key={index} index={index} collateral={collateral} />
          })}
        </Grid>
      )}
    </Box>
  )
}
